// When deployed as a static web app, API calls should be relative to the current origin
// The reverse proxy (nginx or Azure Static Web Apps) handles routing /api/* to the backend
export const API_URL = '';

export interface JDBCConnection {
  url: string;
  driver: string;
  table: string;
  user: string;
  password: string;
}

export interface Transformation {
  id: string;
  type: string;
  config: any;
}

export interface Pipeline {
  pipeline_id: string;
  name: string;
  description?: string;
  job_type?: string; // 'spark' (default) or 'report'
  report_id?: string; // Required when job_type is 'report'
  source: any; // generic connector shape (jdbc or directory sources) - not used for report jobs
  transformations: Transformation[];
  target: any; // generic connector shape; currently typically jdbc with connection + mode - not used for report jobs
  status: string;
  version?: string;
  schedule?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface ChainStep {
  pipeline_id: string;
  depends_on?: string[] | null;
  requires_spark?: boolean;
  requires_container?: string | null;
}

export interface Chain {
  chain_id: string;
  name: string;
  description?: string | null;
  steps: ChainStep[];
  continue_after_failure?: boolean;
  cleanup_resources_on_completion?: boolean;
  created_at: string;
  updated_at: string;
}

export interface PipelineVersionInfo {
  pipeline_id: string;
  active_version: string | null;
  draft_version: string | null;
}

export interface ChainWithVersions {
  chain_id: string;
  name: string;
  description?: string | null;
  steps: ChainStep[];
  continue_after_failure?: boolean;
  cleanup_resources_on_completion?: boolean;
  created_at: string;
  updated_at: string;
  pipeline_versions: Record<string, PipelineVersionInfo>;
}

export interface ChainRun {
  chain_id: string;
  run_id: string;
  status: string;
  started_at: string;
  step_jobs: { pipeline_id: string; job_id: string; status: string }[];
}

// Authentication types
export interface User {
  user_id: string;
  email: string;
  username: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_superuser: boolean;
  roles: string[];
  sso_provider_id?: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

export interface Role {
  role_id: string;
  name: string;
  description?: string;
  permissions: string[];
  is_system_role: boolean;
  created_at: string;
  updated_at: string;
}

export interface Permission {
  permission_id: string;
  resource_type: string;
  action: string;
  description?: string;
  created_at: string;
}

export interface SSOProvider {
  provider_id: string;
  name: string;
  provider_type: 'saml' | 'oidc';
  enabled: boolean;
  saml_config?: {
    entity_id: string;
    sso_url: string;
    slo_url?: string;
    x509_cert: string;
    attribute_mapping: Record<string, string>;
  };
  oidc_config?: {
    client_id: string;
    client_secret: string;
    discovery_url: string;
    scopes: string[];
    attribute_mapping: Record<string, string>;
  };
  default_role?: string;
  auto_create_users: boolean;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface Token {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface APIKey {
  key_id: string;
  description?: string;
  created_at: string;
  last_used_at?: string;
  revoked: boolean;
  revoked_at?: string;
  created_by: string;
}

export interface APIKeyCreate {
  description?: string;
}

export interface APIKeyCreateResponse {
  key_id: string;
  key: string;
  description?: string;
  created_at: string;
  message: string;
}

export interface RoleWithPermissions {
  role_id: string;
  name: string;
  description?: string;
  permissions: Array<{
    permission_id: string;
    resource_type: string;
    action: string;
    description?: string;
  }>;
}

export interface CurrentUserPermissionProfile {
  user_id: string;
  email: string;
  username: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_superuser: boolean;
  sso_provider_id?: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
  roles: RoleWithPermissions[];
}

export interface AuditLogEntry {
  event_id: string;
  request_id: string;
  event_type: string;
  occurred_at: string;
  entry_hash?: string;
  prev_hash?: string;
  signature?: string;
  actor?: {
    user_id?: string | null;
    username?: string | null;
    email?: string | null;
    auth_type?: string | null;
    auth_status?: string | null;
    roles?: string[];
    is_superuser?: boolean;
  };
  request?: {
    method?: string;
    path?: string;
    route_path?: string;
    query?: Record<string, unknown>;
    headers?: Record<string, unknown>;
    body?: Record<string, unknown>;
  };
  response?: {
    status_code?: number;
    duration_ms?: number;
    headers?: Record<string, unknown>;
  };
  target?: {
    resource_type?: string;
    resource_id?: string;
  };
  outcome?: {
    result?: string;
    error_type?: string;
    error_message?: string;
  };
  archive?: {
    blob_uri?: string;
    container?: string;
    blob_path?: string;
    archived_at?: string;
  };
}

export interface BreakGlassIncidentEntry {
  version_id: string;
  pipeline_id: string;
  pipeline_name?: string;
  version: string;
  status?: string;
  is_active?: string | boolean;
  created_at: string;
  created_by_user_id?: string;
  created_by_username?: string;
  created_by_email?: string;
  created_by_name?: string;
  activated_at?: string;
  activated_by_user_id?: string;
  activated_by_username?: string;
  activated_by_email?: string;
  activated_by_name?: string;
  details: {
    incident_reference?: string;
    reason_summary?: string;
    impact_description?: string;
    customer_impact?: string;
    mitigation_plan?: string;
    rollback_plan?: string;
    [key: string]: string | undefined;
  };
}

// Artifact types
export interface Artifact {
  name: string;
  path: string;
  description?: string;
  is_directory?: boolean;
  size?: number;
}

export interface JobArtifactsResponse {
  job_id: string;
  artifacts: Artifact[];
}

export interface JobWithArtifacts {
  job_id: string;
  pipeline_id: string;
  pipeline_name: string;
  pipeline_version: string;
  status: string;
  started_at: string;
  completed_at?: string;
  artifacts: Artifact[];
}

// Insights types
export interface InsightMetrics {
  revenue_m1?: number;
  revenue?: number;
  circuits?: number;
  arpu?: number;
  arpu_delta?: number;
  revenue_delta?: number;
  pct_of_group?: number;
  gross_adds?: number;
  churn?: number;
  net_adds?: number;
  gross_volume_key_driver?: string;
  churn_volume_key_driver?: string;
  revenue_add_key_driver?: string;
  material_revenue_add?: number;
  add_revenue_impact_pct?: number;
  revenue_churn_key_driver?: string;
  material_revenue_churn?: number;
  churn_revenue_impact_pct?: number;
}

export interface DimensionFilter {
  field: string;
  value: string;
}

export interface Insight {
  insight_id: string;
  company?: string;
  reporting_period: string;
  insight_type: 'Manual' | 'Generated';
  dimensions: DimensionFilter[];
  headline: string;
  why?: string;
  action?: string;
  metrics?: InsightMetrics;
  tags: string[];
  status: 'active' | 'archived';
  referenced_by_reports: string[];
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface InsightCreate {
  company?: string;
  reporting_period: string;
  insight_type: 'Manual' | 'Generated';
  dimensions: DimensionFilter[];
  headline: string;
  why?: string;
  action?: string;
  metrics?: InsightMetrics;
  tags?: string[];
}
